/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import Head from "next/head";
import { Component } from "react";

import Banner from "../../components/banner";
import AddToCartButton from "../../components/buttons/add-to-cart-button";
import Certifications from "../../components/certifications";
import InStock from "../../components/in-stock";
import Newsletter from "../../components/newsletter";
import RelatedProducts from "../../components/related-products";
import { Slideshow } from "../../components/slideshow";
import CollectionKind from "../../enums/collection-kind";
import ProductKind from "../../enums/product-kind";
import { toAbsolutePageURL } from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class PolishExfoliantLipScrub extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render(): JSX.Element {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/products/polish-exfoliant-lip-scrub",
                                           market);
    const alt = "Lasting Smiles";
    const title = "Polish Exfoliant Lip Scrub · Lasting Smiles";
    const description = "Lasting Smiles Polish Exfoliant Lip Scrub. " +
      "Natural, organic, and fair trade. " +
      "No animal testing. " +
      "PCR and sugarcane material packaging. " +
      "For soft, hydrated, and healthy lips. " +
      "Caring for people and our planet. " +
      "The world's most caring lip care. " +
      "Smile for a cause.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/products/polish-exfoliant-lip-scrub", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/polish-exfoliant-lip-scrub", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/polish-exfoliant-lip-scrub", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products", market),
                    "name": "Products",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products/polish-exfoliant-lip-scrub", market),
                    "name": "Polish Exfoliant Lip Scrub",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl text-center">
              Polish&nbsp;Exfoliant Lip&nbsp;Scrub
            </h1>
            <p className="text-center text-xl">
              Exfoliate, Prep, &amp; Smooth Lips
            </p>
            <div className="flex flex-col sm:flex-row">
              <div className="flex-initial sm:max-w-xs">
                <Slideshow
                  imageAlts={[
                    "Polish Exfoliant Lip Scrub 1 Pack",
                    "Polish Exfoliant Lip Scrub Art 1",
                    "Polish Exfoliant Lip Scrub Art 2",
                    "Polish Exfoliant Lip Scrub Array",
                  ]}
                  imageJpegs={[
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/single.jpg")}`,
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/art1.jpg")}`,
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/art2.jpg")}`,
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/array.jpg")}`,
                  ]}
                  imageWebps={[
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/single.webp")}`,
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/art1.webp")}`,
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/art2.webp")}`,
                    `${require("../../public/images/products/polish-exfoliant-lip-scrub/array.webp")}`,
                  ]}
                />
              </div>
              <div className="flex-1 m-5">
                <p className="text-xl sm:text-left text-center">
                  A three-in-one lip scrub to exfoliate, condition, and polish for smooth and healthy lips.
                </p>
                <p className="my-5">
                  Our vegan Polish Exfoliant Lip Scrub is formulated with organic sugar cane in a conditioning and polishing base of organic sunflower, caster, and coconut oil to polish away dull, flaky skin gently and effectively leaving behind healthier-looking, smoother lips.
                  Blended with orange citrus, argan, and Vitamin E to boost its exfoliation power as well as help relieve dry, chapped lips.
                </p>
                <InStock />
                <AddToCartButton
                  collection={CollectionKind.LipScrub}
                  product={ProductKind.PolishExfoliantLipScrub}
                />
                <Certifications collection={CollectionKind.LipScrub} />
              </div>
            </div>
          </div>
        </section>
        <section className="bg-polish-exfoliant-300 px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <div className="text-center">
              <img
                alt="Polish Exfoliant Icon"
                className="block h-10 mx-auto w-10"
                src={require("../../public/icons/flavors/polish-exfoliant.svg") as string}
              />
              <h2 className="font-medium font-serif text-2xl">
                Organic and Fair&nbsp;Trade Ingredients
              </h2>
            </div>
            <div className="flex flex-col flex-wrap sm:flex-row">
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  Botanical Formula
                </h3>
                <ul className="sm:text-left text-center text-lg">
                  <li>Cane Sugar</li>
                  <li>Sunflower Oil</li>
                  <li>Caster Seed Oil</li>
                  <li>Candelilla Wax</li>
                  <li>Coconut Oil</li>
                  <li>Orange Oil</li>
                  <li>Carnauba Wax</li>
                  <li>Argan Oil</li>
                  <li>Vitamin E</li>
                </ul>
              </div>
              <div className="flex-1 m-5">
                <h3 className="font-medium font-serif sm:text-left text-center text-xl">
                  Ingredients
                </h3>
                <p className="sm:text-left text-center text-lg">
                  Cane Sugar, Helianthus Annuus (Sunflower) Oil, Ricinus Communis (Castor) Seed Oil, Euphorbia Cerifera (Candelilla) Wax, Cocos Nucifera (Coconut) Oil, Citrus Sinensis (Orange) Oil, Copernicia Cerifera (Carnauba) Wax, Argania Spinosa (Argan) Oil, Tocopheral (Vitamin E), Natural Flavor.
                  Certified Organic Ingredients, Certified Fair Trade Ingredient
                </p>
              </div>
            </div>
            <div>
              <h2 className="font-medium font-serif text-2xl">
                Instructions for Use
              </h2>
              <p className="lg sm:text-left text-lg">
                To use Polish Exfoliant Lip Scrub, spread a small amount of lip scrub on your lips.
                Gently exfoliate by rubbing your lips together.
                Allow lip scrub to sit for a few minutes.
                Next, lightly pat your lips with a clean towel.
                As a last step, apply your lip balm.
                Your lips are now smooth and soft.
              </p>
            </div>
          </div>
        </section>
        <RelatedProducts
          collection={CollectionKind.LipBalm}
          skipProduct={ProductKind.VarietyLipBalm}
        />
      </>
    );
  }
}
